import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from '../axios';
import { OrderType } from "../types";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const OrderTypePage: React.FC = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<OrderType[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<OrderType | null>(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState <GridFilterModel>({ items: [] });

    const savedSortKey = "sortOrderTypesList";
    const savedFilterKey = "filterOrderTypesList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/OrderTypes`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        //const payload = { name };

        if (selectedItem?.id == null || selectedItem.id === '') {
            const payload : OrderType = {
                ...selectedItem, id: crypto.randomUUID(), name: selectedItem?.name ?? "",
            };
            await axiosExt.post(`${API_BASE_URL}/OrderTypes`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } else {
            await axiosExt.put(`${API_BASE_URL}/OrderTypes/${selectedItem.id}`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        }

        setOpen(false);
        fetchItems();
    };

    /*const handleDelete = async (id: number) => {
        const token = localStorage.getItem("token");
        await axiosExt.delete(`${API_BASE_URL}/OrderTypes/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchItems();
    };*/

    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/OrderTypes/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const handleEdit = (item: OrderType) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 150 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <div style={{ height: "100%" }}>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Order Types")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedItem({
                                id: "",
                                name: "",
                            });
                            setOpen(true);
                        }}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                    >
                        {t("Add New")}
                    </Button>
                </div>
                <Box sx={{ width: "100%" }}>
                    <DataGrid rows={items} columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]} 
                    />
                </Box>

                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedItem ? t("Edit OrderType") : t("Add OrderType")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={t("Name")}
                            fullWidth
                            value={selectedItem?.name}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev ? { ...prev, name: e.target.value } : null
                            )}
                            margin="dense"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                        <Button onClick={handleSave}>{t("Save")}</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </LayoutAdmin>
    );
};

export default OrderTypePage;
